import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Loader2, Mail, KeyRound, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";

export default function Register() {
  const { lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const redirect = sp.get("redirect") || "/profile";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) {
      setErr(lang === "tr" ? "Şifre en az 8 karakter olmalı" : "Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success(lang === "tr" ? "Hesabın oluşturuldu! 100 ücretsiz API kullanım birimi yüklendi." : "Account created! 100 free API usage units added.");
      navigate(redirect);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="register-page">
      <div className="text-center mb-10">
        <span className="eyebrow text-red-500">{lang === "tr" ? "KAYIT" : "REGISTER"}</span>
        <h1 className="headline text-4xl sm:text-5xl mt-3 text-white">
          {lang === "tr" ? "Hesap aç" : "Create your account"}
        </h1>
        <p className="mt-3 text-neutral-400">
          {lang === "tr" ? "Kayıt olunca 100 ücretsiz API kullanım birimi hediye." : "Receive 100 free API usage units when you create an account."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-neutral-950 border border-neutral-800 rounded-sm p-8" data-testid="register-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-2">
            {lang === "tr" ? "Ad" : "Name"}
          </label>
          <div className="relative">
            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={lang === "tr" ? "Adın Soyadın" : "Your name"}
              className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-600"
              data-testid="register-name"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-2">
            {lang === "tr" ? "E-posta" : "Email"}
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-600"
              data-testid="register-email"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-2">
            {lang === "tr" ? "Şifre (en az 8 karakter)" : "Password (min 8 chars)"}
          </label>
          <div className="relative">
            <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-600"
              data-testid="register-password"
            />
          </div>
        </div>
        {err && (
          <div className="text-sm text-red-400 bg-red-950/30 border border-red-900 rounded-sm px-3 py-2" data-testid="register-error">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-sm uppercase tracking-wider font-bold text-sm transition-colors disabled:opacity-60"
          data-testid="register-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
          {lang === "tr" ? "Kayıt Ol" : "Create Account"}
        </button>
        <p className="text-sm text-center text-neutral-400">
          {lang === "tr" ? "Hesabın var mı?" : "Already have an account?"}{" "}
          <Link to={`/login?redirect=${encodeURIComponent(redirect)}`} className="text-red-500 hover:text-red-400" data-testid="link-login">
            {lang === "tr" ? "Giriş yap" : "Sign in"}
          </Link>
        </p>
      </form>
    </div>
  );
}
